/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineUsers, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useEthnicGroupContext } from "../../context/EthnicGroupContext";
import { pushOneEthnicGroup } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type EthnicGroup = {
  ethnic_group_id: number | string;
  name: string;
  [key: string]: unknown;
};

const addRoute = "/ethnic/group/add";
const editRoute = "/ethnic/group/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    ethnicGroup,
    count,
    fetchEthnicGroups,
    deleteEthnicGroup,
    updateEthnicGroupState,
  } = useEthnicGroupContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!ethnicGroup) fetchEthnicGroups(page, pageSize);
  }, [ethnicGroup]);

  const rows = useMemo<EthnicGroup[] | null>(() => {
    if (!Array.isArray(ethnicGroup)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return ethnicGroup as EthnicGroup[];
    return (ethnicGroup as EthnicGroup[]).filter((e) =>
      e.name?.toString().toLowerCase().includes(q),
    );
  }, [ethnicGroup, query]);

  const columns: Column<EthnicGroup>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineUsers className="h-4 w-4" />
          </span>
          <span className="font-medium text-gray-900">{row.name}</span>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: EthnicGroup) => {
    const response: any = await deleteEthnicGroup(row.ethnic_group_id);
    if (response?.data?.status === 200 && Array.isArray(ethnicGroup)) {
      updateEthnicGroupState(
        (ethnicGroup as EthnicGroup[]).filter(
          (e) => e.ethnic_group_id !== row.ethnic_group_id,
        ),
      );
    }
  };

  const rowActions = (row: EthnicGroup): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneEthnicGroup(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name}?`,
        description:
          "The ethnic group will be removed from any record that references it.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<EthnicGroup>
      title="Ethnic Groups"
      subtitle="Browse, add and manage ethnic-group reference data."
      icon={<HiOutlineUsers className="h-5 w-5" />}
      actions={[{ label: "Add ethnic group", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.ethnic_group_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search ethnic groups…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchEthnicGroups(p, ps);
        },
      }}
      empty="No ethnic groups yet."
    />
  );
};

export default Overview;
